/* Files sent as JSON (2026-10-23): base64 for a workbook or a photo, and a photo made small enough first. Summit's
   requests stop at 5 MB, so a line's photo (sent on to E3 Subs) goes as a JPEG of at most 2400 px a side; a photo the
   browser can't open goes as it is when it's 3.5 MB or under -- except a HEIC, which E3 Subs can't read either: Safari
   opens one itself, and in any other browser it's turned into a JPEG here first (heicToJpeg). */
import { useEffect, useState } from 'react';

/** An ArrayBuffer as base64, a chunk at a time (one big apply overflows the stack). */
export const toBase64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

const MAX_SIDE = 2400;
const MAX_AS_IS = 3.5 * 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

/** A photo the browser can open, as a JPEG of at most MAX_SIDE a side, in base64. */
async function shrunk(file) {
  const bmp = await createImageBitmap(file); // turned the way the camera held it
  const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bmp.width * scale));
  canvas.height = Math.max(1, Math.round(bmp.height * scale));
  canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
  if (bmp.close) bmp.close();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  if (!blob) throw new Error('not drawn');
  return toBase64(await blob.arrayBuffer());
}

/* A HEIC photo as a JPEG, for the browsers that can't open one -- Chrome, Edge and Firefox (Duncan 2026-10-26, on
   Chrome's "This browser can't read HEIC photos": "is this fixable?"). The converter (heic2any, 1.3 MB) downloads the
   first time someone picks one; while it works, the busy photo button says "Converting…" (useConverting). */
let converting = 0;
const watchers = new Set();
const setConverting = (step) => {
  converting += step;
  watchers.forEach((w) => w(converting > 0));
};
/** Whether a HEIC photo is being turned into a JPEG right now. */
export function useConverting() {
  const [on, setOn] = useState(converting > 0);
  useEffect(() => {
    watchers.add(setOn);
    return () => { watchers.delete(setOn); };
  }, []);
  return on;
}
async function heicToJpeg(file) {
  setConverting(1);
  try {
    const { default: heic2any } = await import('heic2any');
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    return Array.isArray(out) ? out[0] : out; // a HEIC can hold several pictures: the first is the photo
  } finally {
    setConverting(-1);
  }
}

/** A photo to send: { contentType, data (base64), takenAt } -- shrunk to a JPEG when the browser can open it. */
export async function photoBody(file) {
  const takenAt = file.lastModified ? new Date(file.lastModified).toISOString() : undefined;
  try {
    return { contentType: 'image/jpeg', data: await shrunk(file), takenAt };
  } catch (e) {
    const type = String(file.type || '').toLowerCase() || (/\.hei[cf]$/i.test(file.name || '') ? 'image/heic' : '');
    if (!PHOTO_TYPES.includes(type)) throw new Error('That isn’t a photo Summit can send. Use a JPEG, PNG or a phone photo.');
    if (type === 'image/heic' || type === 'image/heif') {
      let jpeg;
      try {
        jpeg = await heicToJpeg(file);
      } catch (err) {
        throw new Error('Summit couldn’t read that HEIC photo. Save it as a JPEG and add that instead.');
      }
      return { contentType: 'image/jpeg', data: await shrunk(jpeg), takenAt };
    }
    if (file.size > MAX_AS_IS) throw new Error('That photo is too big to send (3.5 MB at most). Use a smaller one.');
    return { contentType: type === 'image/heif' ? 'image/heic' : type, data: toBase64(await file.arrayBuffer()), takenAt };
  }
}
